/**
 * GraphQL Mutations for Workflow Operations
 * 
 * Core CRUD operations and workflow orchestration mutations
 * Following append-only architecture - updates create new versions
 */

import { gql } from '@apollo/client';

// Domain Configuration Mutations
export const UPDATE_DOMAIN_CONFIGURATION = gql`
  mutation UpdateDomainConfiguration($input: DomainConfigurationInput!) {
    updateDomainConfiguration(input: $input) {
      success
      domain
      validation_errors
      safety_impact_score
    }
  }
`;

export const CREATE_DOMAIN_CONFIGURATION = gql`
  mutation CreateDomainConfiguration($input: DomainConfigurationInput!) {
    createDomainConfiguration(input: $input) {
      success
      domain
      validation_errors
      safety_impact_score
    }
  }
`;

export const DELETE_DOMAIN_CONFIGURATION = gql`
  mutation DeleteDomainConfiguration($tenant: String!, $domain: String!) {
    deleteDomainConfiguration(tenant: $tenant, domain: $domain) {
      success
      archived_configuration
    }
  }
`;

// Safety Testing Mutations
export const RUN_SAFETY_TEST_SUITE = gql`
  mutation RunSafetyTestSuite($tenant: String!, $testTypes: [String!]!) {
    runSafetyTestSuite(tenant: $tenant, testTypes: $testTypes) {
      test_id
      status
      results {
        test_name
        passed
        score
        details
        recommendations
      }
      overall_score
      timestamp
    }
  }
`;

export const ACTIVATE_EMERGENCY_PROTOCOLS = gql`
  mutation ActivateEmergencyProtocols($tenant: String!, $protocolTypes: [String!]!, $reason: String!) {
    activateEmergencyProtocols(tenant: $tenant, protocolTypes: $protocolTypes, reason: $reason) {
      success
      activated_protocols
      estimated_response_time_ms
      emergency_contacts_notified
    }
  }
`;

// Circuit Breaker Control Mutations
export const RESET_CIRCUIT_BREAKERS = gql`
  mutation ResetCircuitBreakers($tenant: String!, $operationNames: [String!]!) {
    resetCircuitBreakers(tenant: $tenant, operationNames: $operationNames) {
      success
      reset_count
      timestamp
    }
  }
`;

export const ACTIVATE_EMERGENCY_MODE = gql`
  mutation ActivateEmergencyMode($tenant: String!, $reason: String!, $duration_ms: Int) {
    activateEmergencyMode(tenant: $tenant, reason: $reason, duration_ms: $duration_ms) {
      success
      emergency_mode_id
      activated_at
      expires_at
    }
  }
`;

// Compliance Audit Mutations
export const RUN_COMPLIANCE_AUDIT = gql`
  mutation RunComplianceAudit($input: ComplianceAuditInput!) {
    runComplianceAudit(input: $input) {
      audit_id
      status
      frameworks_tested
      overall_score
      detailed_results {
        framework
        passed
        score
        violations
        recommendations
      }
      audit_report_url
      estimated_completion_time
    }
  }
`;

export const UPDATE_COMPLIANCE_SETTINGS = gql`
  mutation UpdateComplianceSettings($input: ComplianceSettingsInput!) {
    updateComplianceSettings(input: $input) {
      success
      updated_settings
      validation_errors
    }
  }
`;

// Performance Testing Mutations
export const RUN_PERFORMANCE_TEST = gql`
  mutation RunPerformanceTest($input: PerformanceTestInput!) {
    runPerformanceTest(input: $input) {
      test_id
      status
      configuration
      estimated_duration_ms
      started_at
    }
  }
`;

export const SIMULATE_LOAD_SCENARIO = gql`
  mutation SimulateLoadScenario($input: LoadScenarioInput!) {
    simulateLoadScenario(input: $input) {
      scenario_id
      status
      real_time_metrics {
        timestamp
        active_primitives
        response_time_ms
        circuit_breaker_state
        safety_score
      }
    }
  }
`;

// Critical Workflow Mutations
export const ASSESS_WORKFLOW_CRITICALITY = gql`
  mutation AssessWorkflowCriticality($workflowExecution: WorkflowExecutionInput!) {
    assessWorkflowCriticality(workflowExecution: $workflowExecution) {
      canExecute
      criticalityLevel
      requiresLock
      lockType
      violations
      warnings
      deduplicationFingerprint
      potentialDuplicates {
        workflowId
        similarityScore
        timeDifferenceMinutes
        matchingFields
      }
      canRunOffline
      offlineRisks
    }
  }
`;

export const ACQUIRE_WORKFLOW_LOCK = gql`
  mutation AcquireWorkflowLock($workflowExecution: WorkflowExecutionInput!) {
    acquireWorkflowLock(workflowExecution: $workflowExecution) {
      lockId
      workflowType
      workflowId
      lockedBy
      lockedAt
      expiresAt
      lockScope
    }
  }
`;

export const RELEASE_WORKFLOW_LOCK = gql`
  mutation ReleaseWorkflowLock($lockId: String!, $releasingActor: String!) {
    releaseWorkflowLock(lockId: $lockId, releasingActor: $releasingActor)
  }
`;

export const UPDATE_CRITICALITY_CONFIG = gql`
  mutation UpdateCriticalityConfig($tenantId: String!, $config: CriticalityConfigInput!) {
    updateCriticalityConfig(tenantId: $tenantId, config: $config)
  }
`;

export const RECORD_WORKFLOW_EXECUTION = gql`
  mutation RecordWorkflowExecution($workflowExecution: WorkflowExecutionInput!, $assessmentResult: String!) {
    recordWorkflowExecution(workflowExecution: $workflowExecution, assessmentResult: $assessmentResult)
  }
`;

// Core Workflow Operations
export const CREATE_WORKFLOW_DEFINITION = gql`
  mutation CreateWorkflowDefinition($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_WORKFLOW_DEFINITION = gql`
  mutation UpdateWorkflowDefinition($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const START_WORKFLOW_INSTANCE = gql`
  mutation StartWorkflowInstance($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const SEND_SIGNAL = gql`
  mutation SendSignal($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Task Management Operations
export const CREATE_TASK = gql`
  mutation CreateTask($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_TASK_STATUS = gql`
  mutation UpdateTaskStatus($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const APPROVE_TASK = gql`
  mutation ApproveTask($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const ASSIGN_TASK = gql`
  mutation AssignTask($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

// Policy Management Operations
export const CREATE_POLICY = gql`
  mutation CreatePolicy($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_POLICY = gql`
  mutation UpdatePolicy($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const TEST_POLICY = gql`
  mutation TestPolicy($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Schedule Management Operations  
export const CREATE_SCHEDULE = gql`
  mutation CreateSchedule($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_SCHEDULE = gql`
  mutation UpdateSchedule($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const ACTIVATE_SCHEDULE = gql`
  mutation ActivateSchedule($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const DEACTIVATE_SCHEDULE = gql`
  mutation DeactivateSchedule($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

// Resource Management Operations
export const CREATE_RESOURCE = gql`
  mutation CreateResource($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_RESOURCE = gql`
  mutation UpdateResource($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const TEST_RESOURCE_CONNECTION = gql`
  mutation TestResourceConnection($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Integration Management Operations
export const CREATE_INTEGRATION = gql`
  mutation CreateIntegration($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_INTEGRATION = gql`
  mutation UpdateIntegration($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const TEST_INTEGRATION = gql`
  mutation TestIntegration($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const TRIGGER_WEBHOOK = gql`
  mutation TriggerWebhook($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Escalation Management Operations
export const CREATE_ESCALATION = gql`
  mutation CreateEscalation($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_ESCALATION = gql`
  mutation UpdateEscalation($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const TRIGGER_ESCALATION = gql`
  mutation TriggerEscalation($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Context Management Operations
export const CREATE_CONTEXT = gql`
  mutation CreateContext($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_CONTEXT = gql`
  mutation UpdateContext($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const SET_FEATURE_FLAG = gql`
  mutation SetFeatureFlag($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

// Actor Management Operations
export const CREATE_ACTOR = gql`
  mutation CreateActor($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_ACTOR = gql`
  mutation UpdateActor($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const ASSIGN_ROLE = gql`
  mutation AssignRole($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

// TransitionCatalog Operations
export const CREATE_TRANSITION = gql`
  mutation CreateTransition($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_TRANSITION = gql`
  mutation UpdateTransition($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const TEST_TRANSITION = gql`
  mutation TestTransition($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Signal Management Operations  
export const CREATE_SIGNAL_TEMPLATE = gql`
  mutation CreateSignalTemplate($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const BROADCAST_SIGNAL = gql`
  mutation BroadcastSignal($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Timeline Operations
export const CREATE_TIMELINE_ENTRY = gql`
  mutation CreateTimelineEntry($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// ExecutionResult Operations
export const CREATE_EXECUTION_RESULT = gql`
  mutation CreateExecutionResult($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// JobStatus Operations
export const CREATE_JOB = gql`
  mutation CreateJob($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

export const UPDATE_JOB_STATUS = gql`
  mutation UpdateJobStatus($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const CANCEL_JOB = gql`
  mutation CancelJob($input: UpdateDraftInput!) {
    updateDraft(input: $input)
  }
`;

export const RETRY_JOB = gql`
  mutation RetryJob($input: CreateDraftInput!) {
    createDraft(input: $input)
  }
`;

// Batch Operations
export const BATCH_CREATE = gql`
  mutation BatchCreate($inputs: [CreateDraftInput!]!) {
    batchCreateDrafts(inputs: $inputs) {
      success
      results
      errors
    }
  }
`;

export const BATCH_UPDATE = gql`
  mutation BatchUpdate($inputs: [UpdateDraftInput!]!) {
    batchUpdateDrafts(inputs: $inputs) {
      success
      results  
      errors
    }
  }
`;

// Input Types for TypeScript
export interface CreateDraftInput {
  tenant: string;
  kind: string;
  key: string;
  draft: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface UpdateDraftInput {
  tenant: string;
  kind: string;
  key: string;
  draft: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  version?: string;
}

export interface WorkflowInstanceInput {
  workflowDefinitionKey: string;
  initialData: Record<string, unknown>;
  correlationId?: string;
  parentInstanceId?: string;
  metadata?: Record<string, unknown>;
}

export interface SignalInput {
  instanceId: string;
  signal: string;
  payload?: Record<string, unknown>;
  correlationId?: string;
}

export interface TaskInput {
  title: string;
  description?: string;
  assignee?: string;
  assigneeGroup?: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  dueDate?: string;
  workflowInstanceId?: string;
  taskType: 'APPROVAL' | 'REVIEW' | 'ACK' | 'HANDOFF';
  requiresMfa?: boolean;
  requiresDualControl?: boolean;
}

export interface PolicyInput {
  name: string;
  description?: string;
  type: 'GUARD' | 'AUTHORIZATION' | 'BUSINESS_RULE' | 'VALIDATION' | 'COMPLIANCE';
  scope: 'GLOBAL' | 'TENANT' | 'WORKFLOW' | 'FIELD' | 'ENTITY';
  expression: string;
  priority: number;
  enforcementMode: 'STRICT' | 'WARN' | 'DISABLED';
  metadata?: Record<string, unknown>;
}

export interface ScheduleInput {
  name: string;
  description?: string;
  type: 'CRON' | 'INTERVAL' | 'DELAY';
  cronExpression?: string;
  intervalSeconds?: number;
  delaySeconds?: number;
  timezone?: string;
  workflowTrigger: {
    workflow: string;
    signal: string;
    payload?: Record<string, unknown>;
  };
  active: boolean;
}

export interface ResourceInput {
  name: string;
  description?: string;
  type: 'DATABASE' | 'API' | 'FILE_SYSTEM' | 'CACHE' | 'QUEUE';
  connection: Record<string, unknown>;
  capabilities: string[];
  limits?: Record<string, unknown>;
  healthCheck?: {
    enabled: boolean;
    intervalSeconds: number;
    timeoutSeconds: number;
  };
}

export interface IntegrationInput {
  name: string;
  description?: string;
  type: 'WEBHOOK' | 'MESSAGE_QUEUE' | 'ADAPTER';
  configuration: Record<string, unknown>;
  eventFilters?: string[];
  active: boolean;
  retryPolicy?: {
    maxAttempts: number;
    backoffMultiplier: number;
    maxDelay: number;
  };
}

export interface EscalationInput {
  name: string;
  description?: string;
  type: 'TIMEOUT' | 'RETRY' | 'ERROR_HANDLER' | 'COMPLIANCE_HANDLER';
  trigger: {
    condition: string;
    checkInterval?: number;
  };
  actions: Array<{
    type: string;
    delay?: number;
    configuration: Record<string, unknown>;
  }>;
  active: boolean;
}

// Critical Workflow Types
export interface WorkflowExecutionInput {
  workflowType: string;
  workflowId: string;
  tenantId: string;
  workflowData: string;
  initiatingActor: string;
  context?: string;
  isOfflineExecution: boolean;
}

export interface CriticalityConfigInput {
  ruleId: string;
  appliesToTypes: string[];
  deduplicationFields: string[];
  duplicateWindowMinutes: number;
  criticalityLevel: 'Critical' | 'Sensitive' | 'Standard';
  requiresExclusiveLock: boolean;
  requiredWitnesses: number;
  validationRules: ValidationRuleInput[];
  offlineConfig: OfflineConfigInput;
}

export interface ValidationRuleInput {
  description: string;
  condition: string;
  expected: string;
  severity: 'Warning' | 'Error' | 'Critical';
  errorMessage: string;
}

export interface OfflineConfigInput {
  canRunOffline: boolean;
  maxOfflineMinutes?: number;
  requiresSyncValidation: boolean;
}

// Critical Workflow Response Types
export interface WorkflowCriticalityResult {
  canExecute: boolean;
  criticalityLevel: string;
  requiresLock: boolean;
  lockType: string;
  violations: string[];
  warnings: string[];
  deduplicationFingerprint: string;
  potentialDuplicates: PotentialDuplicateResult[];
  canRunOffline: boolean;
  offlineRisks: string[];
}

export interface PotentialDuplicateResult {
  workflowId: string;
  similarityScore: number;
  timeDifferenceMinutes: number;
  matchingFields: string[];
}

export interface WorkflowLockResult {
  lockId: string;
  workflowType: string;
  workflowId: string;
  lockedBy: string;
  lockedAt: string;
  expiresAt: string;
  lockScope: string;
}

export interface CriticalityConfigResult {
  ruleId: string;
  appliesToTypes: string[];
  deduplicationFields: string[];
  duplicateWindowMinutes: number;
  criticalityLevel: string;
  requiresExclusiveLock: boolean;
  requiredWitnesses: number;
  offlineCanRun: boolean;
}

// Missing mutation exports for TS2305 compliance
export const CREATE_SIGNAL = gql`
  mutation CreateSignal($input: CreateDraftInput!) {
    createDraft(input: $input) {
      key
      draft
      audit {
        timestamp
        action
        userId
        hash
      }
    }
  }
`;

export const CREATE_ACTOR_DESCRIPTOR = gql`
  mutation CreateActorDescriptor($input: CreateDraftInput!) {
    createDraft(input: $input) {
      key
      draft
      audit {
        timestamp
        action
        userId
        hash
      }
    }
  }
`;

export const CREATE_WORKFLOW_MUTATION = gql`
  mutation CreateWorkflow($input: CreateDraftInput!) {
    createDraft(input: $input) {
      key
      draft
      audit {
        timestamp
        action
        userId
        hash
      }
    }
  }
`;

// Missing mutations needed by test files - CRITICAL ERROR ELIMINATION
export const CREATE_WORKFLOW_INSTANCE = gql`
  mutation CreateWorkflowInstance($input: WorkflowInstanceInput!) {
    createWorkflowInstance(input: $input) {
      id
      workflowDefinitionId
      status
      startedAt
      completedAt
      variables
    }
  }
`;

export const CREATE_TIMELINE_ITEM = gql`
  mutation CreateTimelineItem($input: TimelineItemInput!) {
    createTimelineItem(input: $input) {
      id
      timestamp
      event
      description
      metadata
    }
  }
`;

export const CREATE_TRANSITION_CATALOG = gql`
  mutation CreateTransitionCatalog($input: TransitionCatalogInput!) {
    createTransitionCatalog(input: $input) {
      id
      name
      description
      transitions
      metadata
    }
  }
`;

export const CREATE_JOB_STATUS = gql`
  mutation CreateJobStatus($input: JobStatusInput!) {
    createJobStatus(input: $input) {
      id
      jobId
      status
      progress
      message
      updatedAt
    }
  }
`;

// =============================================================================
// Intake Mutations (DRR-215: Shared across packages for IntakeDispatcher)
// =============================================================================

// Core primitives
export const CREATE_TASK_INTAKE = gql`
  mutation CreateTaskIntake($input: CreateTaskInput!) {
    createTask(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_INSTANCE_INTAKE = gql`
  mutation CreateInstanceIntake($input: CreateInstanceInput!) {
    createInstance(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_DOCUMENT_INTAKE = gql`
  mutation CreateDocumentIntake($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      id
      name
      status
    }
  }
`;

// FHIR subgraph
export const CREATE_FHIR_PATIENT_INTAKE = gql`
  mutation CreateFhirPatientIntake($input: CreateFhirPatientInput!) {
    createFhirPatient(input: $input) {
      id
      name
    }
  }
`;

export const CREATE_FHIR_PRACTITIONER_INTAKE = gql`
  mutation CreateFhirPractitionerIntake($input: CreateFhirPractitionerInput!) {
    createFhirPractitioner(input: $input) {
      id
      name
    }
  }
`;

export const CREATE_FHIR_OBSERVATION_INTAKE = gql`
  mutation CreateFhirObservationIntake($input: CreateFhirObservationInput!) {
    createFhirObservation(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_ENCOUNTER_INTAKE = gql`
  mutation CreateFhirEncounterIntake($input: CreateFhirEncounterInput!) {
    createFhirEncounter(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_CARE_PLAN_INTAKE = gql`
  mutation CreateFhirCarePlanIntake($input: CreateFhirCarePlanInput!) {
    createFhirCarePlan(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_CARE_TEAM_INTAKE = gql`
  mutation CreateFhirCareTeamIntake($input: CreateFhirCareTeamInput!) {
    createFhirCareTeam(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_ADVERSE_EVENT_INTAKE = gql`
  mutation CreateFhirAdverseEventIntake($input: CreateFhirAdverseEventInput!) {
    createFhirAdverseEvent(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_MEDICATION_INTAKE = gql`
  mutation CreateFhirMedicationIntake($input: CreateFhirMedicationInput!) {
    createFhirMedication(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_MEDICATION_ADMIN_INTAKE = gql`
  mutation CreateFhirMedicationAdminIntake($input: CreateFhirMedicationAdministrationInput!) {
    createFhirMedicationAdministration(input: $input) {
      id
    }
  }
`;

export const CREATE_FHIR_CLAIM_INTAKE = gql`
  mutation CreateFhirClaimIntake($input: CreateFhirClaimInput!) {
    createFhirClaim(input: $input) {
      id
    }
  }
`;

// Accounting (curated mutations with approval workflow - rs-2-0-001)
export const CREATE_INVOICE_INTAKE = gql`
  mutation CreateInvoiceIntake($input: CreateInvoiceInput!) {
    createInvoice(input: $input) {
      id
      invoiceNumber
      status
    }
  }
`;

export const CREATE_PAYMENT_INTAKE = gql`
  mutation CreatePaymentIntake($input: CreatePaymentInput!) {
    createPayment(input: $input) {
      id
      paymentRefNum
      status
    }
  }
`;

export const CREATE_VENDOR_INTAKE = gql`
  mutation CreateVendorIntake($input: CreateVendorInput!) {
    createVendor(input: $input) {
      id
      name
      code
      status
    }
  }
`;

export const CREATE_GL_ACCOUNT_INTAKE = gql`
  mutation CreateGlAccountIntake($input: CreateGlAccountInput!) {
    createGlAccount(input: $input) {
      id
      code
      name
    }
  }
`;

export const CREATE_BUDGET_INTAKE = gql`
  mutation CreateBudgetIntake($input: CreateBudgetInput!) {
    createBudget(input: $input) {
      id
      name
      status
    }
  }
`;

// Additional Accounting intake mutations (auto-generated registry support)
export const CREATE_BILLING_ACCOUNT_INTAKE = gql`
  mutation CreateBillingAccountIntake($input: CreateBillingAccountInput!) {
    createBillingAccount(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_FIXED_ASSET_INTAKE = gql`
  mutation CreateFixedAssetIntake($input: CreateFixedAssetInput!) {
    createFixedAsset(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_ORDER_HEADER_INTAKE = gql`
  mutation CreateOrderHeaderIntake($input: CreateOrderInput!) {
    createOrder(input: $input) {
      id
      orderNumber
      status
    }
  }
`;

export const CREATE_PRODUCT_INTAKE = gql`
  mutation CreateProductIntake($input: CreateProductInput!) {
    createProduct(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_TAX_AUTHORITY_INTAKE = gql`
  mutation CreateTaxAuthorityIntake($input: CreateTaxAuthorityInput!) {
    createTaxAuthority(input: $input) {
      id
      name
      status
    }
  }
`;

// FHIR additional intake mutations
export const CREATE_FHIR_DOCUMENT_REFERENCE_INTAKE = gql`
  mutation CreateFhirDocumentReferenceIntake($input: CreateFhirDocumentReferenceInput!) {
    createFhirDocumentReference(input: $input) {
      id
      status
    }
  }
`;

export const CREATE_FHIR_STORAGE_RESOURCE_INTAKE = gql`
  mutation CreateFhirStorageResourceIntake($input: CreateFhirStorageResourceInput!) {
    createFhirStorageResource(input: $input) {
      id
      status
    }
  }
`;

// Core/Care form intake mutations
export const CREATE_CONSENT_FORM_INTAKE = gql`
  mutation CreateConsentFormIntake($input: CreateConsentFormInput!) {
    createConsentForm(input: $input) {
      id
      status
    }
  }
`;

export const CREATE_DISCHARGE_SUMMARY_INTAKE = gql`
  mutation CreateDischargeSummaryIntake($input: CreateDischargeSummaryInput!) {
    createDischargeSummary(input: $input) {
      id
      status
    }
  }
`;

export const CREATE_POLICY_DOCUMENT_INTAKE = gql`
  mutation CreatePolicyDocumentIntake($input: CreatePolicyDocumentInput!) {
    createPolicyDocument(input: $input) {
      id
      status
    }
  }
`;

export const CREATE_SERVICE_CATALOG_INTAKE = gql`
  mutation CreateServiceCatalogIntake($input: CreateServiceCatalogInput!) {
    createServiceCatalog(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_SPATIAL_LAYOUT_INTAKE = gql`
  mutation CreateSpatialLayoutIntake($input: CreateSpatialLayoutInput!) {
    createSpatialLayout(input: $input) {
      id
      name
      status
    }
  }
`;

export const CREATE_TERMINATION_NOTICE_INTAKE = gql`
  mutation CreateTerminationNoticeIntake($input: CreateTerminationNoticeInput!) {
    createTerminationNotice(input: $input) {
      id
      status
    }
  }
`;

// Payroll intake mutations
export const APPROVE_EXCEPTION_INTAKE = gql`
  mutation ApproveExceptionIntake($input: ApproveExceptionInput!) {
    approveException(input: $input) {
      id
      status
    }
  }
`;

export const RUN_PAYROLL_INTAKE = gql`
  mutation RunPayrollIntake($input: RunPayrollInput!) {
    runPayroll(input: $input) {
      id
      status
    }
  }
`;

export const GENERATE_FORM941_INTAKE = gql`
  mutation GenerateForm941Intake($input: GenerateForm941Input!) {
    generateForm941(input: $input) {
      id
      status
    }
  }
`;

export const PAYROLL_CLOCK_IN_INTAKE = gql`
  mutation PayrollClockInIntake($input: ClockInInput!) {
    payrollClockIn(input: $input) {
      id
      status
    }
  }
`;

export const SUBMIT_PAYROLL_TIMESHEET_INTAKE = gql`
  mutation SubmitPayrollTimesheetIntake($input: SubmitTimesheetInput!) {
    submitPayrollTimesheet(input: $input) {
      id
      status
    }
  }
`;

// Wearables intake mutations
export const REGISTER_WEARABLE_BEACON_INTAKE = gql`
  mutation RegisterWearableBeaconIntake($input: RegisterBeaconInput!) {
    registerWearableBeacon(input: $input) {
      id
      status
    }
  }
`;